import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { FileScan, ShieldCheck, AlertTriangle, ArrowUpRight, Loader2, ScanLine } from "lucide-react";

/** The case's paper records held in ArchivIQ-Med, shown on the case itself. */
export const ArchivedPaperPanel = ({ caseId, accession }) => {
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [full, setFull] = useState({});

  useEffect(() => {
    api.get("/archive/documents", { params: { case_id: caseId } })
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]));
  }, [caseId]);

  const toggle = async (id) => {
    setOpenId((cur) => (cur === id ? null : id));
    if (!full[id]) {
      try {
        const { data } = await api.get(`/archive/documents/${id}`);
        setFull((f) => ({ ...f, [id]: data }));
      } catch { /* the list row already shows the essentials */ }
    }
  };

  if (rows === null) {
    return <Loader2 className="animate-spin text-slate-400" size={18} />;
  }

  return (
    <section className="border border-slate-200 dark:border-slate-700" data-testid="case-archive-panel">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800">
        <FileScan size={15} className="text-[#134e4a]" />
        <h3 className="text-sm font-semibold">Archived paper for this case</h3>
        <span className="text-xs text-slate-500">ArchivIQ&#8209;Med</span>
        <Link to={`/archive?case_id=${encodeURIComponent(caseId)}&accession=${encodeURIComponent(accession || "")}`}
              data-testid="case-archive-this-paper"
              className="ml-auto inline-flex items-center gap-1 bg-[#134e4a] px-2.5 py-1 text-xs font-semibold text-white hover:bg-[#0f3f3c]">
          <ScanLine size={12} /> Archive this paper
        </Link>
        <Link to="/archive" data-testid="case-archive-open-module"
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#134e4a] hover:underline">
          Open the archive <ArrowUpRight size={12} />
        </Link>
      </div>

      {rows.length === 0 ? (
        <p className="px-3 py-6 text-center text-sm text-slate-500" data-testid="case-archive-empty">
          No scanned paper filed against {accession || "this case"} yet. Use “Archive this paper”
          above to scan a requisition or an outside report straight onto this case.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-700">
          {rows.map((r) => (
            <li key={r.id} className="px-3 py-3" data-testid={`case-archive-row-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={() => toggle(r.id)} data-testid={`case-archive-toggle-${r.id}`}
                        className="text-left text-sm font-semibold hover:text-[#134e4a]">
                  {r.title}
                </button>
                {r.verification === "Verified" ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-900">
                    <ShieldCheck size={11} /> Verified
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900">
                    <AlertTriangle size={11} /> {r.verification}
                  </span>
                )}
                <span className="ml-auto text-xs text-slate-500">
                  {r.doc_type} · {r.page_count} page{r.page_count === 1 ? "" : "s"}
                </span>
              </div>
              {openId === r.id && (
                <div className="mt-3 grid gap-3 lg:grid-cols-2" data-testid={`case-archive-detail-${r.id}`}>
                  <div className="space-y-2">
                    {Array.from({ length: r.page_count }).map((_, i) => (
                      <img key={i} alt={`Page ${i + 1}`} data-testid={`case-archive-page-${r.id}-${i}`}
                           src={`${process.env.REACT_APP_BACKEND_URL}/api/archive/documents/${r.id}/pages/${i}`}
                           className="w-full border border-slate-300 bg-white" />
                    ))}
                  </div>
                  <pre className="max-h-96 overflow-auto whitespace-pre-wrap border border-slate-200 bg-slate-50 p-3 font-mono text-xs text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                    {full[r.id]?.transcript || r.excerpt || "No transcription yet."}
                  </pre>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
